import { EntityEditorControllerApiFactory } from "@eu.mdotm.api/sphere";
import { useApiGen } from "$root/api/hooks";
import { typedUrlForRoute } from "$root/components/PlatformRouter/RoutesDef";
import { Intercom } from "$root/third-party-integrations/initIntercom";
import { useQueryNoRefetch } from "$root/third-party-integrations/react-query/hooks";
import { InfoTooltip } from "$root/widgets-architecture/layout/WidgetsMapper/InfoTooltip";
import { ActionText, CircularProgressBar, Text, TooltipContent } from "@mdotm/shadcn/legacy/components";
import { switchExpr } from "@mdotm/shadcn/legacy/utils";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PortfolioModeAndAum } from "./PortfolioModeAndAum";
import { portfolioRepository } from "./repository";

export type SmallInvestmentSummaryProps = {
	portfolio?: { uuid?: string; reference?: boolean };
	enhanced: boolean;
	asOf?: string;
};

export function SmallInvestmentSummary(props: SmallInvestmentSummaryProps): React.JSX.Element {
	const { portfolio, enhanced, asOf } = props;
	const { t } = useTranslation();

	const editorApi = useApiGen(EntityEditorControllerApiFactory);

	const { data, isLoading, isError } = useQueryNoRefetch(["investmentSummaryData", portfolio?.uuid, enhanced, asOf], {
		enabled: Boolean(portfolio?.uuid),
		queryFn: async () => {
			if (!portfolio?.uuid) {
				throw new Error("missing portfolio");
			}

			if (enhanced) {
				const [enhanceInvestment, { composition }] = await Promise.all([
					portfolioRepository.summary.fetchQuery(portfolio.uuid, { enhanced: true }),
					portfolioRepository.composition.fetchQuery(portfolio.uuid, {
						enhanced: true,
						currentlyContainsNestedPortfolios: false,
						flattenNestedPortfolios: false,
					}),
				]);
				return {
					summary: enhanceInvestment,
					composition,
					upload: undefined,
					type: "enhance" as const,
				};
			}

			const [investment, { composition }] = await Promise.all([
				portfolioRepository.summary.fetchQuery(portfolio.uuid, { enhanced: false, asOf }),
				portfolioRepository.composition.fetchQuery(portfolio.uuid, {
					enhanced: false,
					currentlyContainsNestedPortfolios: false,
					flattenNestedPortfolios: false,
				}),
			]);

			if (
				["REVIEW", "CALCULATING"].includes(investment.status ?? "ERROR") &&
				investment.createdFromUpload &&
				investment.action !== "ENHANCEMENT"
			) {
				//rick: getInvestmentUploadDetails
				const upload = await editorApi.getEditorReviewComposition(
					portfolio.uuid ?? "",
					portfolio.reference ? "TARGET_INVESTMENT" : "INVESTMENT",
				);
				return {
					summary: investment,
					composition,
					upload: upload.data,
					type: "investment" as const,
				};
			}

			return {
				summary: investment,
				composition,
				type: "investment" as const,
			};
		},
	});

	const instrumentsNumber = useMemo(() => {
		if (!data) {
			return null;
		}

		return data.summary.nofInstruments;
	}, [data]);

	if (isLoading) {
		return (
			<div className="bg-white rounded-sm px-2 py-2">
				<div className="flex flex-1 w-full items-center justify-center">
					<CircularProgressBar value="indeterminate" outerDiameter={24} innerDiameter={20} />
				</div>
			</div>
		);
	}

	if (isError || data === undefined) {
		return <></>;
	}

	if (data.summary.reference) {
		return (
			<div className="bg-white rounded-sm px-2 py-2">
				<div className="flex items-center gap-4">
					<div>
						<Text type="Body/M/Book">Currency</Text>
						&nbsp;
						<Text type="Body/M/Bold">{data.summary.baseCurrency ?? "-"}</Text>
					</div>
					<div>
						<Text type="Body/M/Book">Type</Text>
						&nbsp;
						<Text type="Body/M/Bold">{data.summary.benchmarkLabeled ? "Benchmark" : "Reference"}</Text>
					</div>
					<div>
						<Text type="Body/M/Book">Total instruments</Text>
						&nbsp;
						<Text type="Body/M/Bold">{data.summary.nofInstruments ?? "-"}</Text>
					</div>
					<div>
						<Text type="Body/M/Book">Weighting method</Text>
						&nbsp;
						<Text type="Body/M/Bold">{data.summary.fixedWeights ? "Fixed" : "Drifting"}</Text>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="bg-white rounded-sm px-2 py-2 relative">
			<div className="flex items-center gap-4 flex-wrap">
				<div>
					<Text type="Body/M/Book">Currency</Text>
					&nbsp;
					<Text type="Body/M/Bold">{data.summary.baseCurrency ?? "-"}</Text>
				</div>
				<div>
					<Text type="Body/M/Book">Benchmark</Text>
					&nbsp;
					{data.summary.primaryBenchmarkType === "CUSTOM_BENCHMARK" ? (
						<ActionText
							as="a"
							href={typedUrlForRoute("LegacyBenchmark", {
								uuid: data.summary.primaryBenchmarkIdentifier ?? "",
							})}
							type="Body/M/Bold"
						>
							{data.summary.primaryBenchmarkName ?? "-"}
						</ActionText>
					) : data.summary.primaryBenchmarkType === "REFERENCE_INVESTMENT" ? (
						<ActionText
							as="a"
							href={typedUrlForRoute("ReferenceDetails", {
								uuid: data.summary.primaryBenchmarkIdentifier ?? "",
							})}
							type="Body/M/Bold"
						>
							{data.summary.primaryBenchmarkName ?? "-"}
						</ActionText>
					) : (
						<Text type="Body/M/Bold">{data.summary.primaryBenchmarkName ?? "-"}</Text>
					)}
				</div>
				{data.summary.referenceIdentifier && (
					<div>
						<Text type="Body/M/Book">Reference</Text>
						&nbsp;
						{data.summary.referenceType === "REFERENCE_INVESTMENT" ||
						data.summary.referenceType === "CUSTOM_BENCHMARK" ? (
							<ActionText
								as="a"
								href={switchExpr(data.summary.referenceType, {
									REFERENCE_INVESTMENT: () =>
										typedUrlForRoute("ReferenceDetails", {
											uuid: data.summary.referenceIdentifier ?? "",
										}),
									CUSTOM_BENCHMARK: () =>
										typedUrlForRoute("LegacyBenchmark", {
											uuid: data.summary.referenceIdentifier ?? "",
										}),
								})}
								type="Body/M/Bold"
							>
								{data.summary.referenceName ?? "-"}
							</ActionText>
						) : (
							<Text type="Body/M/Bold">{data.summary.referenceName ?? "-"}</Text>
						)}
					</div>
				)}
				{data.summary.universeIdentifier && (
					<div>
						<Text type="Body/M/Book">Universe</Text>
						&nbsp;
						<ActionText
							as="a"
							href={
								data.summary.universeType === "GROUP_UNIVERSE"
									? typedUrlForRoute("UniverseWithGroupsDetails", {
											universeUuid: data.summary.universeIdentifier ?? "",
										})
									: typedUrlForRoute("UniverseDetails", {
											universeUuid: data.summary.universeIdentifier ?? "",
										})
							}
							type="Body/M/Bold"
						>
							{data.summary.universeName ?? "-"}
						</ActionText>
					</div>
				)}
				<div>
					<Text type="Body/M/Book">Total instruments</Text>
					&nbsp;
					<Text type="Body/M/Bold">{instrumentsNumber ?? "-"}</Text>
				</div>
				<div>
					<Text type="Body/M/Book">Weighting method</Text>
					&nbsp;
					<Text type="Body/M/Bold">{data.summary.fixedWeights ? "Fixed" : "Drifting"}</Text>
				</div>
				<div>
					<Text type="Body/M/Book">Portfolio type</Text>
					&nbsp;
					<Text type="Body/M/Bold">
						{data.summary.liveTrading ? t(`PORTFOLIOS.PORTFOLIO_TYPE_LIVE`) : t(`PORTFOLIOS.PORTFOLIO_TYPE_PAPER`)}
					</Text>
				</div>
				<PortfolioModeAndAum {...data.summary} />
			</div>
			{!data.summary.reference && (
				<div className="absolute right-4 inset-y-1/2 -translate-y-1/2 bg-white h-fit">
					<InfoTooltip>
						<TooltipContent>
							<Text type="Body/M/Book">
								Get to know more about each of the steps of the creation and enhancement process. &nbsp;
							</Text>
							<ActionText type="Body/S/Book" onClick={() => Intercom.showArticle("createOrOptimisePortfolio")}>
								Read more.
							</ActionText>
						</TooltipContent>
					</InfoTooltip>
				</div>
			)}
		</div>
	);
}
